// Fetch user activity from on-chain events (newest first, max 100)
// Activities: Activated(1), LevelIncome(3), DirectRefIncome(4),
// GlobalShareClaimed(5), Claimed(6), RoyaltyClaimed(7), AutoUpgrade(9)
//
// Each contract has its own deployBlock — Plan1, Plan2, and Data can be deployed
// at different blocks (e.g., Data is kept across plan redeployments). The scan
// uses the overall min(deployBlock) as the floor and each filter is only queried
// for blocks >= its own deploy block (so we don't waste calls before that contract existed).

import { NETWORK } from "../config/config";

const MAX_EVENTS = 100;
const CHUNK_SIZE = 50000; // blocks per scan
const MAX_CHUNKS = 20;    // safety cap (= ~1M blocks scanned max)

/**
 * Fetch up to 100 most recent activities (newest first) for a given user.
 * Scans backwards from the latest block in chunks, stops when 100 events found
 * OR when every contract's chunk floor has dipped below its own deployBlock.
 */
export async function fetchActivityFromEvents(contracts, provider, userId) {
  if (!contracts || !provider || !userId) return [];

  const latest = await provider.getBlockNumber();
  // Resolve per-contract deploy blocks (with legacy fallback to deployBlock)
  const blocks = NETWORK.deployBlocks || {};
  const legacy = NETWORK.deployBlock || 0;
  const dataMin  = blocks.data  ?? legacy;
  const plan1Min = blocks.plan1 ?? legacy;
  const plan2Min = blocks.plan2 ?? legacy;
  const overallMin = Math.min(dataMin || 0, plan1Min || 0, plan2Min || 0);

  const { plan1, plan2, data } = contracts;
  const filterDefs = [
    { contract: plan1, name: "Activated", parser: parseActivated, plan: 1, minBlock: plan1Min },
    { contract: plan1, name: "DirectRefIncome", parser: parseDirectRef, plan: 1, minBlock: plan1Min },
    { contract: plan1, name: "LevelIncome", parser: parseLevelIncome, plan: 1, minBlock: plan1Min },
    { contract: plan1, name: "AutoUpgrade", parser: parseAutoUpgrade, plan: 1, minBlock: plan1Min },
    { contract: plan1, name: "RoyaltyClaimed", parser: parseRoyalty, plan: 1, minBlock: plan1Min },
    { contract: plan2, name: "Activated", parser: parseActivated, plan: 2, minBlock: plan2Min },
    { contract: plan2, name: "DirectRefIncome", parser: parseDirectRef, plan: 2, minBlock: plan2Min },
    { contract: plan2, name: "LevelIncome", parser: parseLevelIncome, plan: 2, minBlock: plan2Min },
    { contract: plan2, name: "GlobalShareClaimed", parser: parseGlobalShare, plan: 2, minBlock: plan2Min },
    { contract: plan2, name: "AutoUpgrade", parser: parseAutoUpgrade, plan: 2, minBlock: plan2Min },
    { contract: plan2, name: "RoyaltyClaimed", parser: parseRoyalty, plan: 2, minBlock: plan2Min },
    { contract: data, name: "Claimed", parser: parseClaimed, plan: 0, minBlock: dataMin },
  ];

  const collected = [];
  let toBlock = latest;
  let chunks = 0;

  while (toBlock >= overallMin && collected.length < MAX_EVENTS && chunks < MAX_CHUNKS) {
    const fromBlock = Math.max(toBlock - CHUNK_SIZE + 1, overallMin);

    // Capture chunk bounds locally so the closures don't reference mutating outer state
    const chunkFrom = fromBlock;
    const chunkTo = toBlock;

    // Run filters in parallel — skip any whose contract didn't exist yet in this chunk
    const results = await Promise.all(filterDefs.map(async (f) => {
      if (chunkTo < f.minBlock) return [];
      const effectiveFrom = Math.max(chunkFrom, f.minBlock);
      try {
        const flt = f.contract.filters[f.name](userId);
        const events = await f.contract.queryFilter(flt, effectiveFrom, chunkTo);
        return events.map((ev) => f.parser(ev, f.plan)).filter(Boolean);
      } catch {
        return [];
      }
    }));

    for (const r of results) collected.push(...r);
    toBlock = fromBlock - 1;
    chunks++;
  }

  // Sort newest first
  collected.sort((a, b) => {
    if (b.blockNumber !== a.blockNumber) return b.blockNumber - a.blockNumber;
    return (b.logIndex || 0) - (a.logIndex || 0);
  });

  // Cap at MAX_EVENTS
  const top = collected.slice(0, MAX_EVENTS);

  // Fetch block timestamps for the top events only
  const blockNums = [...new Set(top.map((e) => e.blockNumber))];
  const blockTimes = {};
  await Promise.all(blockNums.map(async (b) => {
    try {
      const block = await provider.getBlock(b);
      blockTimes[b] = block ? block.timestamp : 0;
    } catch { blockTimes[b] = 0; }
  }));

  for (const ev of top) ev.timestamp = blockTimes[ev.blockNumber] || 0;

  return top;
}

// ── Parsers ──
function parseActivated(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 1, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(), level: 0, fromUserId: 0 };
}
function parseLevelIncome(ev, plan) {
  // Plan1 contract emits lvl=pkg+1 for pkg>=1 and lvl=0 for pkg=0.
  // Treat the activity "level" as the matrix depth from buyer to recipient (1-indexed via +1 in UI):
  //   Plan1: level = pkg  (so pkg 0 → L1 direct sponsor, pkg 3 → L4, etc.)
  //   Plan2: level = lvl  (0-indexed 0..9 → L1..L10)
  const pkg = ev.args.pkg.toNumber();
  const lvl = ev.args.lvl.toNumber();
  const storedLevel = plan === 1 ? pkg : lvl;
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 3, plan, amount: ev.args.amt, pkg,
    level: storedLevel, fromUserId: ev.args.fromId.toNumber() };
}
function parseDirectRef(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 4, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(),
    level: 0, fromUserId: ev.args.fromId.toNumber() };
}
function parseGlobalShare(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 5, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(), level: 0, fromUserId: 0 };
}
function parseClaimed(ev) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 6, plan: 0, amount: ev.args.amount, pkg: 0, level: 0, fromUserId: 0 };
}
function parseRoyalty(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 7, plan, amount: ev.args.amt, pkg: 0, level: 0, fromUserId: 0 };
}
function parseAutoUpgrade(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 9, plan, amount: ev.args.amt, pkg: ev.args.toPkg.toNumber(), level: 0, fromUserId: 0 };
}
